import {
  Alert,
  Box,
  Button,
  Divider,
  FormControl,
  Stack,
  Typography,
} from '@benchlabs/react-design-system';
import { isBefore } from 'date-fns';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FieldPath, FormProvider, useForm } from 'react-hook-form';
import { useQueryClient } from 'react-query';
import { FeatureValue, useFeatureFlags } from '@bench/ff-client';
import { SplitFeatureFlags } from '../../../../features';
import { addOnTypeLabels } from './addOnLabels';
import { ErrorWithData } from 'task-manager/bench-services-api/ErrorWithData';
import { useCreateBusinessService } from './api/useCreateBusinessService';
import { useEditBusinessService } from './api/useEditBusinessService';
import { errorMessages } from './errorMessages';
import { SelectInput } from './components/SelectInput';
import { setupBusinessServicesStyles } from './SetupBusinessServices.styles';
import { TextInput } from './components/TextInput';
import { BusinessService, FormValues, GlobalService } from './types';
import { FindGlobalServices } from '../find-global-services/FindGlobalServices';

interface SetupBusinessServiceFormProps {
  addOn: {
    id: string;
    name: string;
    accountType: string;
    dateActive: string;
    dateInactive: string;
  };
  businessId: number;
  businessService?: BusinessService;
  globalService?: GlobalService;
  onCancel: () => void;
  onSuccess: (message: { title: string; message: string }) => void;
}

export const SetupBusinessServiceForm = ({
  addOn,
  businessService,
  businessId,
  globalService,
  onCancel,
  onSuccess,
}: SetupBusinessServiceFormProps) => {
  // Suppress unused variable warning - globalService is part of the interface but not currently used
  void globalService;
  const [globalServiceDeleted, setGlobalServiceDeleted] = useState(false);
  const queryClient = useQueryClient();
  const [
    {
      featureContext: { features },
    },
  ] = useFeatureFlags();
  const useGraphQL =
    features[SplitFeatureFlags.USE_NEW_BENCH_SERVICES]?.value === FeatureValue.FEATURE_ON;

  const formProps = useForm<FormValues>({
    defaultValues: {
      addOnName: addOn.name,
      addOnType: addOnTypeLabels[addOn.accountType] ?? 'Merchant Processor',
      globalServiceId: businessService?.globalServiceId,
      uniqueIdentifier: businessService?.uniqueIdentifier,
      effectiveStartDate: addOn.dateActive, // always default to whatever date the add-on is set to
      effectiveEndDate: addOn.dateInactive, // always default to whatever date the add-on is set to
      addOnDateActive: addOn.dateActive,
      addOnDateInactive: addOn.dateInactive,
    },
  });

  // Update form values when businessService data arrives
  useEffect(() => {
    if (businessService) {
      formProps.reset({
        addOnName: addOn.name,
        addOnType: addOnTypeLabels[addOn.accountType] ?? 'Merchant Processor',
        globalServiceId: businessService.globalServiceId,
        uniqueIdentifier: businessService.uniqueIdentifier,
        effectiveStartDate: addOn.dateActive, // always default to whatever date the add-on is set to
        effectiveEndDate: addOn.dateInactive, // always default to whatever date the add-on is set to
        addOnDateActive: addOn.dateActive,
        addOnDateInactive: addOn.dateInactive,
      });
    }
  }, [businessService, addOn, formProps]);

  const handleApiError = (error: Error) => {
    if (error instanceof ErrorWithData) {
      if (isInvalidInputError(error.data)) {
        error.data.metadata.messages.forEach(err => {
          formProps.setError(err.input, {
            type: 'manual',
            message: errorMessages[err.message] ?? err.message,
          });
        });
        return;
      }
    }
    formProps.setError('root', {
      type: 'manual',
      message: error.message || 'Something went wrong.',
    });
  };

  const {
    mutateAsync: createAsync,
    status: createStatus,
    reset: resetCreate,
  } = useCreateBusinessService({
    handleError: handleApiError,
    useGraphQL,
  });

  const {
    mutateAsync: editAsync,
    status: editStatus,
    reset: resetEdit,
  } = useEditBusinessService({
    handleError: handleApiError,
    useGraphQL,
  });

  const onSubmit = async (data: FormValues) => {
    // Reset mutations and clear errors before attempting submission
    resetCreate();
    resetEdit();
    formProps.clearErrors('root');

    if (
      data.effectiveEndDate &&
      data.effectiveStartDate &&
      isBefore(new Date(data.effectiveEndDate), new Date(data.effectiveStartDate))
    ) {
      formProps.setError('effectiveEndDate', {
        type: 'manual',
        message: 'End date must be after start date',
      });
      return;
    }

    // Convert empty string to null so backend treats it as clearing the value
    const uniqueIdentifier = data.uniqueIdentifier?.trim() || null;

    try {
      if (businessService) {
        await editAsync({
          businessServiceId: businessService.businessServiceId,
          businessId,
          globalServiceId: data.globalServiceId,
          effectiveStartDate: data.effectiveStartDate,
          effectiveEndDate: data.effectiveEndDate,
          uniqueIdentifier,
          addOnId: addOn.id,
        });
      } else {
        await createAsync({
          businessId,
          globalServiceId: data.globalServiceId,
          effectiveStartDate: data.effectiveStartDate,
          effectiveEndDate: data.effectiveEndDate,
          uniqueIdentifier,
          addOnId: addOn.id,
        });
      }
      // Invalidate the query cache so fresh data is fetched on next edit
      await queryClient.invalidateQueries(['getBusinessService', { addOnId: addOn.id }]);
      onSuccess({
        title: 'Bench service has been saved',
        message: 'The service for this add-on has been saved successfully.',
      });
    } catch (error) {
      // Error is already handled by handleApiError, but we catch it here to prevent propagation
    }
  };

  const clearGlobalServiceIdError = useMemo(
    () => () => {
      formProps.formState.errors.globalServiceId && formProps.clearErrors('globalServiceId');
    },
    [formProps],
  );

  const handleGlobalServiceSelected = useCallback(
    (selectedGlobalService: GlobalService) => {
      formProps.setValue('globalServiceId', selectedGlobalService.globalServiceId);
      clearGlobalServiceIdError();
      if (selectedGlobalService.status === 'DELETED') {
        setGlobalServiceDeleted(true);
        formProps.setError('globalServiceId', {
          type: 'manual',
          message: errorMessages.GLOBAL_SERVICE_DELETED,
        });
      } else {
        setGlobalServiceDeleted(false);
      }
    },
    [formProps, clearGlobalServiceIdError],
  );

  const getStatus = () => {
    if ([createStatus, editStatus].includes('loading')) {
      return 'loading';
    }
    return '';
  };

  const uniqueIdentifierValue = formProps.watch('uniqueIdentifier');
  const globalServiceIdValue = formProps.watch('globalServiceId');

  useEffect(() => {
    clearGlobalServiceIdError();
    // Clear errors when user makes changes
    if (formProps.formState.errors.root) {
      formProps.clearErrors('root');
    }
    if (formProps.formState.errors.uniqueIdentifier) {
      formProps.clearErrors('uniqueIdentifier');
    }
  }, [uniqueIdentifierValue, globalServiceIdValue, clearGlobalServiceIdError, formProps]);

  return (
    <FormProvider {...formProps}>
      <form
        id="setup-business-service"
        name="setup-business-service"
        /* eslint-disable-next-line @typescript-eslint/no-misused-promises */
        onSubmit={formProps.handleSubmit(onSubmit)}
      >
        <Box sx={setupBusinessServicesStyles}>
          <FormControl>
            <Stack>
              <Box>
                <Typography variant="h3" fontWeight="bold" sx={{ marginBottom: '16px' }}>
                  Add-on Reference
                </Typography>
                <Stack>
                  <TextInput id="addOnName" name="addOnName" label="NAME" disabled />
                  <SelectInput id="addOnType" label="TYPE" disabled />
                </Stack>
              </Box>
              <Divider />
              <Box>
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="h3" fontWeight="bold">
                    Bench Service Provided
                  </Typography>
                </Box>
                <Typography>
                  Bench services are all the possible things we do for a client. Each Add-on should
                  correspond to one of the services we do. A unique identifier is needed when
                  clients have two instances of the same service eg. two loans with the same bank
                  need processing.
                </Typography>
              </Box>
              {formProps.formState.errors.globalServiceId?.message ? (
                <Alert severity="error">{formProps.formState.errors.globalServiceId.message}</Alert>
              ) : null}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <Box>
                  <FindGlobalServices
                    globalServiceId={businessService?.globalServiceId}
                    onGlobalServiceSelected={handleGlobalServiceSelected}
                  />
                </Box>
                <TextInput id="uniqueIdentifier" label="UNIQUE ID" placeholder="Optional" />
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  width: '100%',
                  gap: '20px',
                  marginTop: '24px',
                }}
              >
                <Button variant="outlined" size="small" fullWidth onClick={onCancel}>
                  CANCEL
                </Button>
                <Button
                  variant="contained"
                  className="bktools-primary"
                  type="submit"
                  size="small"
                  fullWidth
                  disabled={getStatus() === 'loading'}
                >
                  SAVE
                </Button>
              </Box>
              {formProps.formState.errors.root?.message ? (
                <Alert severity="error">{formProps.formState.errors.root.message}</Alert>
              ) : null}
            </Stack>
          </FormControl>
        </Box>
      </form>
    </FormProvider>
  );
};

interface InvalidInputError extends Record<string, unknown> {
  code: 'INVALID_INPUT';
  message: string;
  name: string;
  metadata: {
    messages: { input: FieldPath<FormValues>; message: string }[];
  };
}

function isInvalidInputError(error: Record<string, unknown>): error is InvalidInputError {
  return 'code' in error && error.code === 'INVALID_INPUT' && 'metadata' in error;
}
